import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import NoteForm from "./NoteForm";
import useNewsAdminServer from "./useNewsAdminServer";
import {
    fetchAdminNoteByTitle,
    fetchAdminNoteOptions,
    formatAdminPublishDate,
    mapAdminNoteToDraft,
    updateAdminNote,
} from "./api";
import { resolveAdminCategoryValue } from "./config";

export default function FispTvAdminEditNote() {
    const { server, serverResolved } = useNewsAdminServer();
    const [noteOptions, setNoteOptions] = useState([]);
    const [selectedTitle, setSelectedTitle] = useState("");
    const [draft, setDraft] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [loadingNote, setLoadingNote] = useState(false);

    useEffect(() => {
        if (!serverResolved) {
            return undefined;
        }

        const controller = new AbortController();
        setLoadingOptions(true);
        setFeedback(null);

        fetchAdminNoteOptions(server, controller.signal)
            .then((items) => setNoteOptions(items))
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setNoteOptions([]);
                setFeedback({ type: "error", message: "No pudimos cargar el listado de notas." });
            })
            .finally(() => setLoadingOptions(false));

        return () => controller.abort();
    }, [server, serverResolved]);

    useEffect(() => {
        if (!selectedTitle) {
            setDraft(null);
            setLoadingNote(false);
            return undefined;
        }

        const controller = new AbortController();
        setLoadingNote(true);
        setFeedback(null);
        setDraft(null);

        fetchAdminNoteByTitle(selectedTitle, server, controller.signal)
            .then((result) => {
                if (!result?.raw) {
                    setDraft(null);
                    setFeedback({ type: "error", message: "No encontramos la nota seleccionada." });
                    return;
                }

                setDraft(mapAdminNoteToDraft(result.raw));
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setDraft(null);
                setFeedback({ type: "error", message: "No pudimos cargar la nota seleccionada." });
            })
            .finally(() => setLoadingNote(false));

        return () => controller.abort();
    }, [selectedTitle, server]);

    const notesOptions = useMemo(() => {
        return noteOptions.map((item) => ({
            value: item.title,
            label: item.title,
        }));
    }, [noteOptions]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!draft?.id) {
            return;
        }

        setSubmitting(true);
        setFeedback(null);

        try {
            const videoUrl = draft.videoUrl.trim();

            const payload = {
                id: draft.id,
                titulo: draft.title.trim(),
                categoria: resolveAdminCategoryValue(draft.category) || draft.category.trim(),
                descripcion: draft.description.trim(),
                imagen: draft.image.trim(),
                imagenPortada: draft.coverImage.trim() || null,
                linkVideo: videoUrl || " ",
                cuerpoNota: draft.body,
                url: draft.slug.trim(),
                fecha: formatAdminPublishDate(draft.publishDate, draft.publishTime),
                artRel1: "",
                artRel2: "",
                artRel3: "",
            };

            await updateAdminNote(payload, server).catch((error) => {
                if (error?.message && error.message.includes("No se pudo obtener")) {
                    throw error;
                }
            });

            setFeedback({ type: "success", message: "La nota se actualizó correctamente. Recargando..." });

            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } catch {
            setFeedback({ type: "error", message: "No pudimos actualizar la nota. Intentá nuevamente." });
            setSubmitting(false);
        }
    };

    if (loadingNote) {
        return (
            <AdminLayout title="Editar nota" pageClassName="tv-admin-edit-note-page">
                <div className="tv-admin-fullscreen-spinner" aria-live="polite" aria-busy="true">
                    <span className="tv-admin-spinner" aria-hidden="true"></span>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Editar nota" pageClassName="tv-admin-edit-note-page">
            <section className="tv-admin-picker-card">
                <select
                    id="tv-admin-note-picker"
                    value={selectedTitle}
                    onChange={(event) => setSelectedTitle(event.target.value)}
                    disabled={!serverResolved || loadingOptions}
                >
                    <option value="">
                        {loadingOptions ? "Cargando notas..." : noteOptions.length ? "Elegir una nota" : "No hay notas disponibles"}
                    </option>
                    {notesOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </select>
            </section>

            {feedback ? <div className={`tv-admin-feedback is-${feedback.type}`}>{feedback.message}</div> : null}

            {draft ? (
                <NoteForm
                    draft={draft}
                    setDraft={setDraft}
                    allNotes={[]}
                    onSubmit={handleSubmit}
                    submitLabel="Actualizar nota"
                    submitBusyLabel="Actualizando..."
                    submitting={submitting}
                    disabled={!serverResolved}
                    showSlugField={false}
                    showImageUrlField={false}
                    showRelatedFields={false}
                    showPublishSummary={false}
                    allowImageUploadOnly={true}
                />
            ) : null}
        </AdminLayout>
    );
}
